import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bookmark, Users, Send, ImageIcon } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export const EMOJIS = ["❤️", "😂", "🥹", "🔥", "👏", "💍"];
const device = () => {
  let d = localStorage.getItem("lif_device");
  if (!d) { d = `dev_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; localStorage.setItem("lif_device", d); }
  return d;
};

function SaveHighlight({ story, onOpenChange }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { onOpenChange(open); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [titles, setTitles] = useState([]);
  const [title, setTitle] = useState("");
  useEffect(() => { if (open) api.get("/weddings/mine/highlight-titles").then((r) => setTitles(r.data)).catch(() => {}); }, [open]);
  async function save(t) {
    try { await api.post(`/weddings/mine/live-stories/${story.story_id}/highlight`, { title: t }); toast.success(`Saved to "${t}" highlight`); setOpen(false); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  if (!story.expires_at) return null;
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs bg-white/15 backdrop-blur rounded-full px-3 py-1.5" data-testid="story-save-highlight"><Bookmark className="w-3.5 h-3.5" /> Save to highlight</button>
      {open && (
        <div className="absolute bottom-10 right-0 w-60 bg-white text-[var(--ink)] rounded-xl p-3 shadow-xl" data-testid="story-highlight-menu">
          {titles.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2">{titles.map((t) => <button key={t} onClick={() => save(t)} className="text-xs border border-[var(--line)] rounded-full px-2.5 py-1 hover:border-[var(--gold)]" data-testid={`story-highlight-${t}`}>{t}</button>)}</div>}
          <form onSubmit={(e) => { e.preventDefault(); title.trim() && save(title.trim()); }} className="flex gap-1.5">
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={24} placeholder="New, e.g. Proposal" className="input-luxe text-xs flex-1 py-1.5" data-testid="story-highlight-title" />
            <button className="btn-gold text-xs px-3" data-testid="story-highlight-create">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}

function StoryReply({ story, onTyping }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(""); }, [story.story_id]);
  async function send(e) {
    e.preventDefault();
    if (!text.trim()) return;
    let name = localStorage.getItem("lif_name") || "";
    if (!name) { name = window.prompt("Your name, so the couple knows who it's from") || ""; if (name.trim()) localStorage.setItem("lif_name", name.trim()); }
    setBusy(true);
    try { await api.post(`/stories/${story.story_id}/reply`, { message: text.trim(), name, device_id: device() }); setText(""); toast.success("Sent privately to the couple"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); onTyping(false); }
  }
  return (
    <form onSubmit={send} className="w-full max-w-sm flex gap-2" data-testid="story-reply-form">
      <input value={text} onChange={(e) => setText(e.target.value)} onFocus={() => onTyping(true)} onBlur={() => !text && onTyping(false)} maxLength={500}
        placeholder="Send the couple a private message…" className="flex-1 bg-black/45 backdrop-blur border border-white/30 rounded-full px-4 py-2 text-sm text-white placeholder:text-white/60 outline-none focus:border-[var(--gold)]" data-testid="story-reply-input" />
      <button disabled={busy || !text.trim()} className="w-10 h-10 rounded-full bg-[var(--gold)] text-black flex items-center justify-center disabled:opacity-40" aria-label="Send reply" data-testid="story-reply-send"><Send className="w-4 h-4" /></button>
    </form>
  );
}

function SetCover({ story }) {
  async function setCover() {
    try { await api.put("/weddings/mine/highlight-cover", { title: story.highlight || "Highlights", story_id: story.story_id }); toast.success(`Cover set for "${story.highlight || "Highlights"}"`); window.dispatchEvent(new Event("lif-highlights-changed")); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return <button onClick={setCover} className="flex items-center gap-1 text-xs bg-white/15 backdrop-blur rounded-full px-3 py-1.5 text-white" data-testid="story-set-cover"><ImageIcon className="w-3.5 h-3.5" /> Make cover</button>;
}

export function StoryReactions({ story, onPause = () => {} }) {
  const [r, setR] = useState(null);
  const [pop, setPop] = useState(null);
  const [who, setWho] = useState(false);
  const [menu, setMenu] = useState(false);
  const [typing, setTyping] = useState(false);
  useEffect(() => { onPause(who || menu || typing); }, [who, menu, typing]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setR(null); setWho(false); api.get(`/stories/${story.story_id}/reactions`).then((x) => setR(x.data)).catch(() => {}); }, [story.story_id]);
  async function send(emoji) {
    setPop({ emoji, k: Date.now() });
    try { const { data } = await api.post(`/stories/${story.story_id}/react`, { emoji, device_id: device(), name: localStorage.getItem("lif_name") || "" }); setR(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  if (!r) return null;
  return (
    <div className="absolute bottom-20 inset-x-0 z-20 flex flex-col items-center gap-2 px-4" onClick={(e) => e.stopPropagation()} data-testid="story-reactions">
      {pop && <span key={pop.k} className="absolute -top-16 text-5xl animate-[storyPop_1s_ease-out_forwards] pointer-events-none">{pop.emoji}</span>}
      {who && r.is_owner && (
        <div className="w-full max-w-xs max-h-40 overflow-y-auto bg-black/70 backdrop-blur rounded-xl p-3 text-xs text-white space-y-1" data-testid="story-reactors">
          {r.people.length ? r.people.map((p, i) => <p key={i} className="flex justify-between"><span>{p.name}</span><span>{p.emoji}</span></p>) : <p className="text-white/60">No reactions yet</p>}
        </div>
      )}
      <div className="flex items-center gap-1.5 bg-black/45 backdrop-blur rounded-full px-2 py-1.5">
        {EMOJIS.map((e, i) => (
          <button key={e} onClick={() => send(e)} className="relative w-10 h-10 text-xl rounded-full hover:bg-white/15 active:scale-90 transition-transform" aria-label={`React ${e}`} data-testid={`story-react-${i}`}>
            {e}{r.counts[e] > 0 && <span className="absolute -top-1 -right-1 text-[9px] leading-none min-w-[14px] h-[14px] flex items-center justify-center bg-[var(--gold)] text-black rounded-full px-1 font-bold">{r.counts[e]}</span>}
          </button>
        ))}
      </div>
      {r.is_owner && (
        <div className="flex gap-2">
          <button onClick={() => setWho(!who)} className="flex items-center gap-1 text-xs bg-white/15 backdrop-blur rounded-full px-3 py-1.5 text-white" data-testid="story-who-reacted"><Users className="w-3.5 h-3.5" /> {r.total} reacted</button>
          <SaveHighlight story={story} onOpenChange={setMenu} />
          {!story.expires_at && <SetCover story={story} />}
        </div>
      )}
      {!r.is_owner && <StoryReply story={story} onTyping={setTyping} />}
    </div>
  );
}
